import { useMemo } from 'react';
import { distanceM } from '../lib/geo';
import { holeFrame } from '../lib/holeFrame';
import type { Hole, LngLat } from '../lib/types';

/**
 * One hole, drawn straight up the screen.
 *
 * Satellite imagery shows the whole course at once, which is exactly the
 * problem: on a buggy screen in the sun you cannot tell at a glance which of
 * the eighteen green shapes is yours. So this draws ONE hole as a schematic —
 * tee at the bottom, green at the top, everything else gone — and rotates the
 * world so the hole always plays vertically no matter which way it points.
 *
 * SVG rather than a map canvas on purpose: it is resolution-independent (the
 * "HD clear holes" requirement is free), it costs no tile fetches on a course
 * with no signal, and the same component serves the phone, the buggy screen and
 * the CarPlay window.
 *
 * 🟥 On the CarPlay base view this must render the MAP ONLY. Apple bans
 * "windows, alerts, panels, overlays, or user interface elements" from the base
 * view, and their own example bans lane guidance — navigation data — from it.
 * Yardage readouts therefore live in `rails`, which the phone and buggy draw
 * and CarPlay omits in favour of templates. See docs/CARPLAY_COMPLIANCE.md.
 */

export interface HoleSchematicProps {
  hole: Hole;
  teeColour: string;
  /** The golfer, if we have a fix. Drawn as the blue dot. */
  position?: LngLat | null;
  /** Aim point, drawn as the yellow line from the golfer. */
  aim?: LngLat | null;
  /** Corridor half-width in metres. 45 m ≈ a generous fairway plus rough. */
  corridorM?: number;
  className?: string;
}

/** Viewport in SVG units. The hole is fitted into this box with a margin. */
const VB_W = 100;
const VB_H = 200;
const MARGIN = 10;

const HAZARD_FILL: Record<string, string> = {
  bunker: '#f0c419',   // yellow — sand
  water: '#3f7fe0',    // blue — water
  ob: '#e0523f',       // red — out of bounds and everything else to avoid
  layup: '#8a8f98',
};

export default function HoleSchematic({
  hole, teeColour, position, aim, corridorM = 45, className,
}: HoleSchematicProps) {
  const geom = useMemo(() => {
    const tee = hole.tees[teeColour] ?? Object.values(hole.tees)[0];
    const green = hole.green.middle;
    if (!tee || !green) return null;

    // Tee at the origin, green straight above it, whichever way the hole
    // actually points. Sign-critical and covered by holeFrame.test.ts.
    const project = holeFrame(tee, green);
    type Pt = { x: number; y: number };

    const centre = (hole.centreline?.length ? hole.centreline : [tee, green]).map(project);
    const teeP = project(tee);
    const greenP = {
      front: project(hole.green.front),
      middle: project(green),
      back: project(hole.green.back),
    };
    const hazards = hole.hazards.map((h) => ({
      ...h,
      p: project(h.point),
      // A mapped outline beats a circle: it shows whether you are catching the
      // corner of a bunker or the middle of it.
      poly: h.ring && h.ring.length >= 3 ? h.ring.map(project) : null,
    }));
    const greenRing = hole.green.ring && hole.green.ring.length >= 3
      ? hole.green.ring.map(project) : null;
    const playerP = position ? project(position) : null;
    const aimP = aim ? project(aim) : null;

    /*
     * Fit to the HOLE, not to the golfer.
     *
     * Including the player unconditionally means that standing on the 5th while
     * looking at the 12th zooms out far enough to fit both, and the hole you
     * are actually reading shrinks to a sliver. So the player only earns a say
     * in the framing when they are somewhere near the hole; otherwise the hole
     * is framed on its own and the dot is simply off-screen, which is the
     * honest picture — you are not on this hole.
     */
    const xs = [
      ...centre, teeP, greenP.front, greenP.back,
      ...hazards.flatMap((h) => h.poly ?? [h.p]),
      ...(greenRing ?? []),
    ];
    const holeSpan = Math.hypot(greenP.middle.x - teeP.x, greenP.middle.y - teeP.y);
    const playerNear = playerP
      ? Math.min(
          ...centre.map((c) => Math.hypot(c.x - playerP.x, c.y - playerP.y)),
        ) < Math.max(holeSpan * 0.5, 150)
      : false;
    if (playerP && playerNear) xs.push(playerP);
    const minX = Math.min(...xs.map((p) => p.x)) - corridorM;
    const maxX = Math.max(...xs.map((p) => p.x)) + corridorM;
    const minY = Math.min(...xs.map((p) => p.y)) - 25;
    const maxY = Math.max(...xs.map((p) => p.y)) + 25;

    const scale = Math.min(
      (VB_W - MARGIN * 2) / Math.max(maxX - minX, 1),
      (VB_H - MARGIN * 2) / Math.max(maxY - minY, 1),
    );
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const view = (p: Pt): Pt => ({
      x: VB_W / 2 + (p.x - cx) * scale,
      y: VB_H / 2 + (p.y - cy) * scale,
    });

    return {
      scale,
      centre: centre.map(view),
      tee: view(teeP),
      green: { front: view(greenP.front), middle: view(greenP.middle), back: view(greenP.back) },
      hazards: hazards.map((h) => ({ ...h, v: view(h.p), poly: h.poly?.map(view) ?? null })),
      greenRing: greenRing?.map(view) ?? null,
      // Off-hole players are not drawn at all: a dot pinned to a corner
      // implies a position it does not have.
      player: playerP && playerNear ? view(playerP) : null,
      aimV: aimP ? view(aimP) : null,
      greenRadius: Math.max(distanceM(hole.green.front, hole.green.back) / 2, 8) * scale,
      corridor: corridorM * scale,
    };
  }, [hole, teeColour, position, aim, corridorM]);

  if (!geom) {
    return <div className={`hs-empty ${className ?? ''}`}>Hole not mapped</div>;
  }

  const path = geom.centre.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');

  return (
    <svg
      className={`hole-schematic ${className ?? ''}`}
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`Hole ${hole.number}, par ${hole.par}`}
    >
      {/*
        The corridor IS the blackout: rather than dimming the other seventeen
        holes, only this one's playing corridor is ever drawn. A thick round
        stroke along the centreline gives a dogleg a fairway that bends with it,
        which a rectangle cannot do.
      */}
      <path
        d={path}
        className="hs-corridor-edge"
        strokeWidth={geom.corridor * 2}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d={path}
        className="hs-corridor"
        strokeWidth={Math.max(geom.corridor * 2 - 1.6, 1)}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* The play line: tee through the dogleg to the pin. Thin, so it reads as
          a suggestion rather than as the edge of anything. */}
      <path d={path} className="hs-playline" fill="none" />

      {/* Hazards. Sized by nothing but legibility — these are carry points in
          the data, not surveyed outlines, so drawing them as precise shapes
          would claim an accuracy we do not have. */}
      {geom.hazards.map((h) => (h.poly ? (
        <polygon
          key={h.id}
          points={h.poly.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ')}
          fill={HAZARD_FILL[h.kind] ?? HAZARD_FILL.layup}
          className={`hs-hazard hs-hazard-${h.kind}`}
        />
      ) : (
        <circle
          key={h.id}
          cx={h.v.x}
          cy={h.v.y}
          r={h.kind === 'water' ? 5 : 3.4}
          fill={HAZARD_FILL[h.kind] ?? HAZARD_FILL.layup}
          className={`hs-hazard hs-hazard-${h.kind}`}
        />
      )))}

      {/* The green, and the pin at its middle. */}
      {geom.greenRing ? (
        <polygon
          points={geom.greenRing.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ')}
          className="hs-green"
        />
      ) : (
        <circle
          cx={geom.green.middle.x}
          cy={geom.green.middle.y}
          r={geom.greenRadius}
          className="hs-green"
        />
      )}
      <circle cx={geom.green.middle.x} cy={geom.green.middle.y} r={1.4} className="hs-pin" />

      {/* Tee. */}
      <circle cx={geom.tee.x} cy={geom.tee.y} r={1.8} className="hs-tee" />

      {/* Aim line — yellow, from the golfer, because aiming is done from where
          you stand and not from the tee you already left. */}
      {geom.player && geom.aimV && (
        <>
          <line
            x1={geom.player.x} y1={geom.player.y}
            x2={geom.aimV.x} y2={geom.aimV.y}
            className="hs-aim"
          />
          <circle cx={geom.aimV.x} cy={geom.aimV.y} r={1.6} className="hs-aim-dot" />
        </>
      )}

      {/* The golfer. Drawn last so nothing can cover it. */}
      {geom.player && (
        <>
          <circle cx={geom.player.x} cy={geom.player.y} r={4.2} className="hs-me-halo" />
          <circle cx={geom.player.x} cy={geom.player.y} r={2.2} className="hs-me" />
        </>
      )}
    </svg>
  );
}
